package com.routexk.quiz.funnel

import com.routexk.quiz.i18n.t
import com.routexk.quiz.i18n.tv

/**
 * All funnel copy. English + Spanish; the active language is resolved per read
 * via [t]/[tv] (see com.routexk.quiz.i18n.I18n), so screens localize reactively.
 */
object FunnelCopy {

    // Hero
    val HERO_TITLE_LINE1 get() = t("More Routes.", "Más rutas.")
    val HERO_TITLE_LINE2 get() = t("More Money.", "Más dinero.")
    val HERO_BULLET_LINE1 get() = t("Cut extra miles.", "Recorta millas de más.")
    val HERO_BULLET_LINE2 get() = t("Earn up to \$15,000 more per year", "Gana hasta \$15,000 más al año")
    val HERO_CTA get() = t("CALCULATE HOW IN 1 MIN", "CALCÚLALO EN 1 MIN")
    val HERO_NOW_LABEL get() = t("Now", "Ahora")
    val HERO_WITH_LABEL get() = t("With RouteXK", "Con RouteXK")
    val HERO_CARD_TITLE get() = t("Extra revenue / mo", "Ingreso extra / mes")
    val HERO_NOW_VALUE get() = "\$0"
    val HERO_WITH_VALUE get() = "+\$4,642"
    val HERO_WITH_BADGE get() = "+154%"

    // Quiz common
    val CONTINUE get() = t("CONTINUE", "CONTINUAR")
    val CONFIRM get() = t("CONFIRM", "CONFIRMAR")
    val TRUSTED_BY get() = t("Trusted by 1,211,650+ drivers", "Con la confianza de 1,211,650+ conductores")
    val CITY_HELPER_EMOJI get() = "👀"
    val CITY_HELPER get() = t(
        "Help us personalize your earnings maximization potential based on your area",
        "Ayúdanos a personalizar tu potencial de ganancias según tu zona",
    )

    // AI analysis
    val ANALYSIS_TITLE get() = t("AI analysis", "Análisis con IA")
    /** Rotating status lines shown while the analysis progress runs 0→100%. */
    val ANALYSIS_STEPS get() = listOf(
        t("Analyzing your daily route patterns...", "Analizando tus patrones de ruta diarios..."),
        t("Scanning industry benchmarks for your profession...", "Escaneando referencias del sector para tu profesión..."),
        t("Calculating your time & fuel loss per year...", "Calculando tu pérdida anual de tiempo y combustible..."),
        t("Identifying your biggest optimization opportunities...", "Identificando tus mayores oportunidades de optimización..."),
        t("Comparing with 2.4M similar driver profiles...", "Comparando con 2.4M de perfiles de conductores similares..."),
        t("Running AI route efficiency simulation...", "Ejecutando simulación de eficiencia de rutas con IA..."),
        t("Measuring your income recovery potential...", "Midiendo tu potencial de recuperación de ingresos..."),
        t("Building your personalized RouteXK plan...", "Creando tu plan personalizado de RouteXK..."),
    )
    val ANALYSIS_RATING get() = t("Rated 4.8/5.0", "Calificación 4.8/5.0")

    // Results (tax reveal)
    val RESULTS_BADGE get() = t("ANALYSIS COMPLETE", "ANÁLISIS COMPLETO")
    val RESULTS_TITLE_PART1 get() = t("Look, ", "¡Mira, deducciones ")
    val RESULTS_TITLE_ACCENT get() = "EXTRA"
    val RESULTS_TITLE_PART2 get() = t(" tax deductions to claim!", " de impuestos por reclamar!")
    val RESULTS_CARD_TITLE get() = t("Estimated deduction for this year", "Deducción estimada para este año")
    val RESULTS_CARD_PILL get() = t(
        "Ultimate tax deduction tool, with tracking and one-click claim from the IRS",
        "La herramienta definitiva de deducción, con seguimiento y reclamo al IRS con un clic",
    )
    val RESULTS_CAPTION get() = t("Based on your mileage and work schedule", "Según tu millaje y horario de trabajo")
    val RESULTS_FOUND_TITLE get() = t("HERE'S WHAT WE'VE FOUND", "ESTO ES LO QUE ENCONTRAMOS")
    val RESULTS_FOUND_1 get() = t(
        "The IRS lets self-employed drivers deduct business mileage at \$0.76/mile",
        "El IRS permite a los conductores independientes deducir \$0.76 por milla de trabajo",
    )
    val RESULTS_FOUND_2 get() = t(
        "Your real earnings are 23% higher than you think",
        "Tus ganancias reales son 23% más altas de lo que crees",
    )
    val RESULTS_FOUND_3 get() = t(
        "Our AI has prepared a step-by-step guide to help you claim every dollar you're owed",
        "Nuestra IA preparó una guía paso a paso para reclamar cada dólar que te corresponde",
    )
    val RESULTS_CTA get() = t("CLAIM NOW", "RECLAMAR AHORA")

    // Email capture
    val EMAIL_TITLE get() = t("Enter your email to claim the tax savings", "Ingresa tu correo para reclamar el ahorro de impuestos")
    val EMAIL_PLACEHOLDER get() = t("Enter your email", "Ingresa tu correo")
    val EMAIL_PROOF get() = t("Over 1.2 million users use us daily", "Más de 1.2 millones de usuarios nos usan a diario")
    val EMAIL_ERROR get() = t("Please enter a valid email", "Ingresa un correo válido")

    // Report
    val REPORT_TITLE get() = t("Your personalized efficiency report summary", "Resumen de tu reporte de eficiencia personalizado")
    val REPORT_SUBTITLE get() = t(
        "BASED ON 3M+ ROUTE PROS ANALYZED BY ROUTEXK AI",
        "BASADO EN 3M+ PROFESIONALES DE RUTAS ANALIZADOS POR ROUTEXK AI",
    )
    val REPORT_PROFILE_TITLE get() = t("YOUR ROUTE PROFILE", "TU PERFIL DE RUTA")
    val REPORT_PROFILE_SUB get() = t("Based on your quiz", "Según tu cuestionario")
    val REPORT_MONEY_TITLE get() = t("More in your pocket", "Más en tu bolsillo")
    val REPORT_MONEY_PILL get() = t("Without a single extra hour of work", "Sin una sola hora extra de trabajo")
    val REPORT_HOURS_TITLE get() = t("Hours saved per year", "Horas ahorradas al año")
    val REPORT_FUEL_TITLE get() = t("Lower fuel spend", "Menos gasto en combustible")
    val REPORT_CTA get() = t("GET MY PLAN", "OBTENER MI PLAN")
    val REPORT_SCENARIO_A get() = t("Take the time back", "Recupera el tiempo")
    val REPORT_SCENARIO_B get() = t("Or fill it with work", "O llénalo de trabajo")
    val REPORT_OR get() = t("OR", "O")
    val CLAIMED_BADGE get() = t("Tax Deduction Guide (%s) Claimed", "Guía de Deducción de Impuestos (%s) Reclamada")

    // Paywall
    val TIMER_TITLE get() = t("Your discount reserved for", "Tu descuento reservado por")
    val TIMER_BADGE get() = t("60% OFF", "60% DE DESCUENTO")
    val PAYWALL_TITLE get() = t(
        "Activate your RouteXK access now and start earning more from day one",
        "Activa tu acceso a RouteXK ahora y gana más desde el primer día",
    )
    val POTENTIAL_TIME get() = t("Time saving potential", "Potencial de ahorro de tiempo")
    val POTENTIAL_FUEL get() = t("Fuel saving potential", "Potencial de ahorro de combustible")
    val POTENTIAL_MONEY get() = t("Money increase potential", "Potencial de aumento de ingresos")
    val POTENTIAL_MONEY_SHORT get() = t("Money increase", "Aumento de ingresos")
    val POTENTIAL_HIGH get() = t("HIGH", "ALTO")
    val POTENTIAL_MEDIUM get() = t("MEDIUM", "MEDIO")
    val POTENTIAL_EXTREMELY_HIGH get() = t("EXTREMELY HIGH", "EXTREMADAMENTE ALTO")
    val ESTIMATE_CARD_TITLE get() = t("Estimated money increase", "Aumento de ingresos estimado")
    val ESTIMATE_CARD_NOTE get() = t(
        "Based on 99% of RouteXK users with a similar profile, results typically visible within the first 6 weeks",
        "Según el 99% de los usuarios de RouteXK con un perfil similar, los resultados suelen verse en las primeras 6 semanas",
    )
    val GUARANTEE get() = t("100% Money-Back Guarantee", "Garantía de devolución del 100%")
    val GUARANTEE_SUB get() = t(
        "If you're not satisfied, we'll refund your payment in full — no questions asked",
        "Si no estás satisfecho, te devolvemos el pago completo — sin preguntas",
    )
    val PER_WEEK get() = t("per week", "por semana")
    val MOST_POPULAR get() = t("MOST POPULAR", "MÁS POPULAR")
    val PAYWALL_CTA get() = t("ACTIVATE MY ROUTEXK ACCESS", "ACTIVAR MI ACCESO A ROUTEXK")

    // Trial packages: each plan is a paid trial that renews into the main
    // $49.99/month subscription once the trial period ends.
    const val RENEWAL_CENTS = 4999L
    /** Shown under each plan's price, e.g. "then $49.99/mo". */
    val PLAN_RENEWAL_NOTE get() = t("then \$49.99/mo", "luego \$49.99/mes")
    /** Full disclosure shown at checkout (%1 = trial length, %2 = renewal price). */
    val TRIAL_DISCLOSURE get() = t(
        "After your %1\$s trial, your RouteXK subscription renews at %2\$s/month. Cancel anytime.",
        "Al terminar tu prueba de %1\$s, tu suscripción a RouteXK se renueva a %2\$s/mes. Cancela cuando quieras.",
    )
    /** Trial length inside the disclosure, e.g. "2-week" / "1-month" / "1-year". */
    fun trialLengthLabel(days: Int): String = when {
        days >= 360 -> t("1-year", "1 año")
        days in 28..31 -> t("1-month", "1 mes")
        else -> {
            val w = (days / 7).coerceAtLeast(1)
            t("$w-week", "$w semanas")
        }
    }
    /** Plan row label, e.g. "2 weeks plan" / "1 month plan" / "1 year plan". */
    fun planLabel(days: Int): String = when {
        days >= 360 -> t("1 year plan", "plan de 1 año")
        days in 28..31 -> t("1 month plan", "plan de 1 mes")
        else -> {
            val weeks = (days / 7).coerceAtLeast(1)
            val w = if (weeks == 1) t("1 week", "1 semana") else t("$weeks weeks", "$weeks semanas")
            t("$w plan", "plan de $w")
        }
    }

    // Checkout
    val CHANGE_PLAN get() = t("Change plan", "Cambiar plan")
    val EXPRESS_CHECKOUT get() = t("Express checkout", "Pago exprés")
    val OR_PAY_WITH_CARD get() = t("or pay with card", "o paga con tarjeta")
    val CARD_NUMBER get() = t("Card number", "Número de tarjeta")
    val EXPIRE_DATE get() = t("Expiration date", "Fecha de vencimiento")
    val SECURITY_CODE get() = t("Security code", "Código de seguridad")
    val COUNTRY get() = t("Country / Territory", "País / Territorio")
    val DISCOUNT_LINE get() = t("Price discount", "Descuento")
    val TOTAL get() = t("Total:", "Total:")
    val CHECKOUT_CTA get() = t("CONTINUE", "CONTINUAR")
    val PAYMENT_ERROR get() = t(
        "Payment failed. Please try again or use another payment method.",
        "El pago falló. Inténtalo de nuevo o usa otro método de pago.",
    )
    /** Checkout summary line, e.g. "Your 2 weeks trial". */
    fun yourPlan(label: String): String = t("Your $label", "Tu $label")

    // Success
    val SUCCESS_BADGE get() = t("Subscription Activated", "Suscripción activada")
    val SUCCESS_BADGE_MOBILE get() = t("Payment successful", "Pago exitoso")
    val SUCCESS_TITLE_DARK get() = t("Your plan is active", "Tu plan está activo")
    val SUCCESS_TITLE_DARK_MOBILE get() = t("Your route is ready", "Tu ruta está lista")
    val SUCCESS_TITLE_ACCENT get() = t("Open the app now", "Abre la app ahora")
    val SUCCESS_GET_APP get() = t("GET THE APP", "OBTENER LA APP")
    val SUCCESS_STEP_1 get() = t("Scan the QR code with your phone", "Escanea el código QR con tu teléfono")
    val SUCCESS_STEP_2 get() = t("Install the app", "Instala la app")
    val SUCCESS_STEP_3 get() = t("Start optimizing your routes and saving money", "Empieza a optimizar tus rutas y a ahorrar dinero")

    // Testimonials (shared between AI analysis, paywall and checkout)
    data class Testimonial(val name: String, val timeAgo: String, val text: String)

    private val TESTIMONIALS_EN = listOf(
        Testimonial(
            "Jake M.", "1 week ago",
            "Been using this for 3 months and honestly didn't expect to save this much on gas. My weekly fuel costs dropped like crazy",
        ),
        Testimonial(
            "Carlos R.", "1 week ago",
            "I do 6 client visits a day and I swear I'm spending way less on gas than before. The savings add up fast",
        ),
        Testimonial(
            "James R.", "1 week ago",
            "Saved around \$200 on fuel last month alone. Did not expect the difference to be that significant",
        ),
        Testimonial(
            "Laura S.", "1 week ago",
            "My mileage deduction this tax season was the highest it's ever been. Finally tracking everything properly",
        ),
        Testimonial(
            "Omar J.", "1 week ago",
            "Between fuel savings and the mileage deductions I track now, I'm coming out significantly ahead every month",
        ),
    )

    private val TESTIMONIALS_ES = listOf(
        Testimonial(
            "Jake M.", "hace 1 semana",
            "Llevo 3 meses usándolo y de verdad no esperaba ahorrar tanto en gasolina. Mi gasto semanal en combustible bajó muchísimo",
        ),
        Testimonial(
            "Carlos R.", "hace 1 semana",
            "Hago 6 visitas a clientes al día y te juro que gasto mucho menos en gasolina que antes. El ahorro se acumula rápido",
        ),
        Testimonial(
            "James R.", "hace 1 semana",
            "Ahorré como \$200 en combustible solo el mes pasado. No esperaba una diferencia tan grande",
        ),
        Testimonial(
            "Laura S.", "hace 1 semana",
            "Mi deducción por millaje esta temporada de impuestos fue la más alta de todas. Por fin registro todo bien",
        ),
        Testimonial(
            "Omar J.", "hace 1 semana",
            "Entre el ahorro de combustible y las deducciones por millaje que ahora registro, salgo bastante ganando cada mes",
        ),
    )

    val TESTIMONIALS get() = tv(TESTIMONIALS_EN, TESTIMONIALS_ES)
}
